// src/components/FeedbackPage.tsx
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faUndo, 
  faSmileBeam, 
  faTimesCircle, 
  faQuoteLeft, 
  faQuoteRight,
  faSpinner,
  faUser,
  faCalendarAlt,
  faComments
} from '@fortawesome/free-solid-svg-icons';
import { 
  db 
} from '../firebase'; 
import { 
  collection, 
  addDoc, 
  serverTimestamp,
  query,
  onSnapshot,
  orderBy,
  Timestamp,
  limit,
  where
} from 'firebase/firestore';

// --- Interfaces ---

interface Testimony {
  id: string;
  name: string;
  testimony: string;
  createdAt: Timestamp;
}

// --- Styled Components ---

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

const FeedbackPageContainer = styled.div`
  min-height: 100vh;
  padding: 60px 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4efe9 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #6c63ff, #48c6ef, #6c63ff);
    background-size: 200% 100%;
    animation: gradientShift 3s ease infinite;
  }

  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

// ── Testimonials Components ──

const TestimonialsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const TestimonialsHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const MainTitle = styled.h1`
  color: #2d3748;
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 15px;
  background: linear-gradient(135deg, #6c63ff 0%, #48c6ef 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: #718096;
  font-size: 1.2rem;
  line-height: 1.7;
  font-weight: 400;
  max-width: 600px;
  margin: 0 auto;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const TestimonyCard = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  border: 1px solid rgba(255,255,255,0.2);
  display: flex;
  flex-direction: column;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #6c63ff, #48c6ef);
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.12);
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const TestimonyHeader = styled.div`
  font-weight: 700;
  font-size: 1.2rem;
  color: #2d3748;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TestimonyContent = styled.p`
  font-style: italic;
  color: #4a5568;
  line-height: 1.7;
  flex-grow: 1;
  position: relative;
  margin: 20px 0;
  padding: 0 10px;
  
  &::before {
    content: '"';
    font-size: 4rem;
    color: #48c6ef;
    opacity: 0.3;
    position: absolute;
    top: -20px;
    left: -10px;
    line-height: 1;
    font-family: Georgia, serif;
  }
  
  &::after {
    content: '"';
    font-size: 4rem;
    color: #48c6ef;
    opacity: 0.3;
    position: absolute;
    bottom: -40px;
    right: -10px;
    line-height: 1;
    font-family: Georgia, serif;
  }
`;

const TestimonyFooter = styled.div`
  font-size: 0.9rem;
  color: #718096;
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
`;

// ── Form Section ──

const FormSection = styled.div`
  width: 100%;
  max-width: 680px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const FormTitle = styled.h2`
  color: #2d3748;
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 15px;
  position: relative;
  background: linear-gradient(135deg, #6c63ff 0%, #48c6ef 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(90deg, #6c63ff, #48c6ef);
    border-radius: 2px;
  }
`;

const FormSubtitle = styled.p`
  color: #718096;
  font-size: 1.15rem;
  line-height: 1.7;
  font-weight: 400;
  margin-bottom: 30px;
  text-align: center;
`;

const FeedbackCard = styled.div`
  background: #fff;
  border-radius: 20px;
  box-shadow: 
    0 15px 35px rgba(0,0,0,0.08),
    0 5px 15px rgba(0,0,0,0.05);
  padding: 50px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 25px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid rgba(255,255,255,0.2);
  backdrop-filter: blur(10px);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 
      0 25px 50px rgba(0,0,0,0.12),
      0 15px 25px rgba(0,0,0,0.08);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #4a5568;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Input = styled.input`
  padding: 16px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #fff;
  font-family: inherit;
  
  &:focus {
    border-color: #6c63ff;
    box-shadow: 0 0 0 4px rgba(108, 99, 255, 0.1);
    outline: none;
    transform: translateY(-2px);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  &::placeholder {
    color: #a0aec0;
  }
`;

const TextArea = styled.textarea`
  padding: 16px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  min-height: 160px;
  resize: vertical;
  transition: all 0.3s ease;
  background: #fff;
  font-family: inherit;
  line-height: 1.6;
  
  &:focus {
    border-color: #6c63ff;
    box-shadow: 0 0 0 4px rgba(108, 99, 255, 0.1);
    outline: none;
    transform: translateY(-2px);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  &::placeholder {
    color: #a0aec0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
`;

interface ButtonProps {
  $primary?: boolean;
  $loading?: boolean;
}

const Button = styled.button<ButtonProps>`
  background: ${({ $primary, $loading }) => 
    $loading ? '#cbd5e0' : $primary ? 'linear-gradient(135deg, #6c63ff 0%, #48c6ef 100%)' : 'linear-gradient(135deg, #718096 0%, #a0aec0 100%)'};
  color: #fff;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${({ $loading }) => ($loading ? 'not-allowed' : 'pointer')};
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  position: relative;
  overflow: hidden;
  min-width: 120px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: ${({ $loading }) => ($loading ? 'none' : 'translateY(-3px)')};
    box-shadow: ${({ $loading }) => ($loading ? '0 4px 15px rgba(0,0,0,0.1)' : '0 8px 25px rgba(0,0,0,0.15)')};
  }

  &:active {
    transform: translateY(1px);
  }

  & > svg {
    margin-right: 10px;
    transition: transform 0.3s ease;
  }

  &:hover > svg {
    transform: ${({ $loading }) => ($loading ? 'none' : 'translateX(2px)')};
  }
`;

const StatusMessage = styled.div<{ $isError?: boolean }>`
  padding: 20px;
  border-radius: 12px;
  font-weight: 600;
  margin-bottom: 20px;
  color: ${({ $isError }) => ($isError ? '#c53030' : '#2d7d46')};
  background: ${({ $isError }) => ($isError ? 'linear-gradient(135deg, #fed7d7 0%, #feebeb 100%)' : 'linear-gradient(135deg, #c6f6d5 0%, #f0fff4 100%)')};
  border: 1px solid ${({ $isError }) => ($isError ? '#feb2b2' : '#9ae6b4')};
  display: flex;
  align-items: center;
  gap: 12px;
  animation: ${slideIn} 0.4s ease-out;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);

  & > svg {
    font-size: 1.3rem;
    flex-shrink: 0;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  gap: 15px;
  color: #718096;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #718096;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  border: 2px dashed #e2e8f0;
  
  & > svg {
    font-size: 3rem;
    margin-bottom: 20px;
    color: #cbd5e0;
  }
`;

const TestimonialsCount = styled.div`
  background: linear-gradient(135deg, #6c63ff 0%, #48c6ef 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

// ── Main Component ──

export const FeedbackPage: React.FC = () => {
  const [name, setName] = useState('');
  const [testimony, setTestimony] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [testimonials, setTestimonials] = useState<Testimony[]>([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(
      collection(db, 'testimonies'),
      where('approved', '==', true),     // ← Only show approved testimonies
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const fetchedTestimonials: Testimony[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          fetchedTestimonials.push({
            id: doc.id,
            name: data.name || 'Anonymous',
            testimony: data.testimony || 'No testimony provided.',
            createdAt: data.createdAt as Timestamp,
          });
        });
        setTestimonials(fetchedTestimonials);
        setLoadingTestimonials(false);
      }, 
      (err) => {
        console.error("Failed to fetch approved testimonials:", err);
        setError('Failed to load approved testimonials. Please try again later.');
        setLoadingTestimonials(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleReset = () => {
    setName('');
    setTestimony('');
    setError(null);
    setSuccess(false);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!name.trim() || !testimony.trim()) {
      setError('Please provide both your name and testimony.');
      return;
    }

    if (testimony.trim().length < 10) {
      setError('Please provide a more detailed testimony (minimum 10 characters).');
      return;
    }

    setLoading(true);

    try {
      await addDoc(collection(db, 'testimonies'), {
        name: name.trim(),
        testimony: testimony.trim(),
        createdAt: serverTimestamp(),
        approved: false,           // New submissions start as unapproved
      });
      
      setSuccess(true);
      setTimeout(() => {
        handleReset();
      }, 3000);

    } catch (e) {
      console.error("Error adding document: ", e);
      setError('Failed to submit feedback. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: Timestamp) => {
    if (!timestamp) return 'Date unknown';
    return timestamp.toDate().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <FeedbackPageContainer>
      
      {/* Testimonials Section */}
      <TestimonialsContainer>
        <TestimonialsHeader>
          <MainTitle>Community Feedback</MainTitle>
          <Subtitle>
            Discover what others are saying about their experience with GREMA. 
            Join the conversation and share your own story.
          </Subtitle>
          <TestimonialsCount>
            <FontAwesomeIcon icon={faComments} />
            {testimonials.length} {testimonials.length === 1 ? 'Testimony' : 'Testimonies'}
          </TestimonialsCount>
        </TestimonialsHeader>
        
        {loadingTestimonials && (
          <LoadingContainer>
            <FontAwesomeIcon icon={faSpinner} spin size="lg" /> 
            Loading approved community feedback...
          </LoadingContainer>
        )}

        {!loadingTestimonials && testimonials.length === 0 && (
          <EmptyState>
            <FontAwesomeIcon icon={faComments} />
            <h3 style={{ color: '#4a5568', marginBottom: '10px' }}>No Approved Feedback Yet</h3>
            <p style={{ color: '#718096' }}>
              Share your buying and selling experience with GREMA — after approval it will appear here to help others make informed decisions.
            </p>
          </EmptyState>
        )}

        <TestimonialsGrid>
          {testimonials.map((item) => (
            <TestimonyCard key={item.id}>
              <TestimonyHeader>
                <FontAwesomeIcon icon={faUser} color="#6c63ff" />
                {item.name}
              </TestimonyHeader>
              <TestimonyContent>
                {item.testimony}
              </TestimonyContent>
              <TestimonyFooter>
                <span>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '8px' }} />
                  {formatTimestamp(item.createdAt)}
                </span>
              </TestimonyFooter>
            </TestimonyCard>
          ))}
        </TestimonialsGrid>
      </TestimonialsContainer>

      {/* Submission Form Section */}
      <FormSection>
        <FormTitle>Share Your Testimony</FormTitle>
        <FormSubtitle>
          Share your buying and selling experience with GREMA — this will help others make informed decisions
        </FormSubtitle>

        <FeedbackCard>
          {success && (
            <StatusMessage>
              <FontAwesomeIcon icon={faSmileBeam} />
              Thank you! Your testimony has been submitted and is pending approval.
            </StatusMessage>
          )}
          
          {error && (
            <StatusMessage $isError>
              <FontAwesomeIcon icon={faTimesCircle} />
              {error}
            </StatusMessage>
          )}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="name">
                <FontAwesomeIcon icon={faUser} size="sm" />
                Your Name
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your name or business name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading || success}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="testimony">Your Testimony</Label>
              <TextArea
                id="testimony"
                placeholder="Please share your experience here..."
                value={testimony}
                onChange={(e) => setTestimony(e.target.value)}
                required
                disabled={loading || success}
                minLength={10}
              />
            </FormGroup>

            <ButtonContainer>
              <Button 
                type="button" 
                onClick={handleReset} 
                disabled={loading || success}
                $primary={false}
              >
                <FontAwesomeIcon icon={faUndo} /> Clear
              </Button>
              <Button 
                type="submit" 
                $primary
                $loading={loading}
                disabled={loading || success}
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Submitting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} /> Share Testimony
                  </>
                )}
              </Button>
            </ButtonContainer>
          </Form>
        </FeedbackCard>
      </FormSection>

    </FeedbackPageContainer>
  );
};